import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlert, useDoubleConfirm } from '../contexts/AlertContext';

// Felhasználó saját foglalásainak listázása.
export default function MyBookings() {
  const { user } = useAuth();
  const showAlert = useAlert();
  const doubleConfirm = useDoubleConfirm();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [washingPrice, setWashingPrice] = useState(500);

  // Lekérdezzük a foglalásokat és a beállításokat, majd a mosási díj alapján frissítjük a végösszeget.
  useEffect(() => {
    const loadAppointments = async () => {
      if (!user?.id) return;

      try {
        const [response, settingsResponse] = await Promise.all([
          axios.get(`/api/users/appointments?userId=${user.id}`),
          axios.get('/api/settings').catch(() => null)
        ]);
        const configuredWashingPrice = Number.parseInt(settingsResponse?.data?.washing_price, 10);
        setWashingPrice(Number.isNaN(configuredWashingPrice) ? 500 : configuredWashingPrice);

        const activeAppointments = (response.data || [])
          .filter(appointment => appointment.status !== 'Lemondva')
          .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
          .slice(0, 5);
        setAppointments(activeAppointments);
      } catch (error) {
        console.error('Foglalások betöltési hiba:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAppointments();
  }, [user]);

  // Számok formázásához magyarra.
  const formatPrice = (value) => {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) return '0';
    return Math.round(amount).toLocaleString('hu-HU');
  };

  // Foglalás lemondása.
  const cancelAppointment = async (id) => {
    if (!await doubleConfirm('Biztosan le szeretnéd mondani ezt a foglalást?', 'A foglalás véglegesen törlődik. Biztosan folytatod?')) return;

    try {
      await axios.delete(`/api/bookings/${id}`);
      setAppointments(current => current.filter(appointment => appointment.id !== id));
      showAlert('Foglalás sikeresen lemondva! 🚫');
    } catch (error) {
      showAlert('Hiba a lemondás során: ' + (error.response?.data?.error || 'Ismeretlen hiba'));
    }
  };

  // Státusz alapján kártyák szinezése.
  // Kész foglalás = zöld
  // Függőben lévő = narancssárga
  const getStatusStyle = (status) => {
    if (status === 'Kész' || status === 'confirmed') {
      return 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
    }
    return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 dark:border-blue-900 dark:border-t-blue-400"></div>
      </div>
    );
  }

  // UI betöltés:
  // - betöltési spinner, ha még nem érkezett meg a backend válasza
  // - foglalási kártyák lista, ha vannak adatok
  // - üres állapot, ha a felhasználónak nincs foglalása
  return (
    <div className="min-h-screen bg-transparent text-gray-900 dark:text-white transition-colors duration-300">
      <div className="max-w-4xl mx-auto p-4 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 sm:mb-12">
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Profil</p>
            <h1 className="text-3xl sm:text-5xl font-bold text-gray-800 dark:text-white">Foglalásaim</h1>
          </div>
          <Link to="/profil" className="px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
            Vissza a profilhoz
          </Link>
        </div>

        <div className="bg-slate-100/5 dark:bg-slate-800/60 rounded-3xl p-5 sm:p-10 shadow-lg border border-slate-200/80 dark:border-slate-700/80">
          <div className="space-y-4">
            {appointments.length > 0 ? (
              appointments.map(appointment => {
                const statusStyles = getStatusStyle(appointment.status);
                const isCompleted = appointment.status === 'Kész' || appointment.status === 'confirmed';
                const servicePrice = Number.parseInt(appointment.price, 10);
                const totalPrice = (Number.isNaN(servicePrice) ? 0 : servicePrice)
                  + (Number(appointment.with_washing) === 1 ? washingPrice : 0);

                return (
                  <div key={appointment.id} className="p-5 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-2xl border border-slate-300/90 dark:border-slate-600 shadow-sm hover:shadow-md transition-all">
                    <div className="flex flex-wrap justify-between items-start gap-2 mb-3">
                      <div className="font-bold text-base sm:text-lg text-gray-800 dark:text-white">
                        {new Date(appointment.start_time).toLocaleDateString('hu-HU')}{' '}
                        {new Date(appointment.start_time).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="flex items-center gap-2 ml-auto">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusStyles}`}>
                          {isCompleted ? '✅ Kész' : '🕒 Függőben'}
                        </span>
                        {!isCompleted && (
                          <button
                            onClick={() => cancelAppointment(appointment.id)}
                            className="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                            title="Foglalás lemondása"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <div>
                        <h4 className="font-semibold text-base text-gray-700 dark:text-gray-300">{appointment.service_name}</h4>
                      </div>
                      <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                        {formatPrice(washingPrice) + appointment.price ? formatPrice(totalPrice) : '---'} Ft
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-16">
                <h2 className="text-xl font-bold text-gray-600 dark:text-gray-400 mb-4">Még nincsenek foglalásaid</h2>
                <Link to="/foglalas" className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-2xl font-bold inline-block transition-colors">Első foglalás</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
